import Axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Tab,
  Tabs,
} from "react-bootstrap";
import Container from "react-bootstrap/Container";
import Modal from "react-bootstrap/Modal";
import {
  FaEye,
  FaGlobe,
  FaMoon,
  FaSun,
  FaTimes,
  FaGripVertical,
} from "react-icons/fa";
import calendarAvatar from "../assets/images/user/calendar-avatar.png";
import routineAvatar from "../assets/images/user/rutine-avatar.png";
import useStoredPermissions from "./useStoredPermissions";
import { DataTablePagination } from "./utils/dataTablePagination";
import { DropDownClients } from "./clients/dropDownClients";
import { DropDownDepartments } from "./departments/dropDownDepartments";
import ClipLoader from "react-spinners/ClipLoader";
import { SideBar } from "./sideBar";
import { NavBar } from "./navBar";
import {
  getApiResponseMessage,
  getApiErrorMessage,
} from "../utils/apiMessageHelper";
// Contexts
import { useNotification } from "../contexts/NotificationContext";
import { useAuth } from "../contexts/AuthContext";
// Utils
import formatDate from "../utils/formatDate";

export const MainCalendar = () => {
  const { t } = useTranslation();
  const permissions = useStoredPermissions() || [];
  const { clientId } = useAuth();
  const { notify } = useNotification();

  // Show sidebar if user has clientId (both Super Admin and Client Admin)
  const hasPermissions = clientId !== null && clientId !== undefined;

  // Check if user is Super Admin (clientId === 1)
  const isSuperAdmin = clientId === 1;

  //> States
  const [data, setData] = useState([]);
  const [calendarRows, setCalendarRows] = useState([]);
  const [currentCalendar, setCurrentCalendar] = useState([]);
  const [newCalendar, setNewCalendar] = useState({});
  const [currentCalendarDay, setCurrentCalendarDay] = useState("Mon");
  const [CalendarValidated, setCalendarValidated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editingCalendarId, setEditingCalendarId] = useState(null);

  // Selected client for Super Admin (default to their own clientId for Client Admin)
  const [selectedClientId, setSelectedClientId] = useState(
    isSuperAdmin ? null : clientId,
  );

  // Selected department for calendar creation (optional)
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);

  // Routine-related states
  const [routineRows, setRoutineRows] = useState([]);
  const [routineDays, setRoutineDays] = useState({
    Sun: [],
    Mon: [],
    Tue: [],
    Wed: [],
    Thu: [],
    Fri: [],
    Sat: [],
  });

  //> Modals
  const [showNewCalendar, setShowNewCalendar] = useState(false);
  const [showCurrentCalendar, setShowCurrentCalendar] = useState(false);
  const [showDeleteCalendarConfirm, setShowDeleteCalendarConfirm] =
    useState(false);

  const handleCloseNewCalendar = () => {
    setShowNewCalendar(false);
    setNewCalendar({});
    setSelectedDepartmentId(null);
    setEditingCalendarId(null);
    setRoutineDays({
      Sun: [],
      Mon: [],
      Tue: [],
      Wed: [],
      Thu: [],
      Fri: [],
      Sat: [],
    });
    setCalendarValidated(false);
  };

  const handleShowNewCalendar = () => {
    setEditingCalendarId(null);
    setNewCalendar({ name: "", startDate: "", endDate: "" });
    setSelectedDepartmentId(null);
    setRoutineDays({
      Sun: [],
      Mon: [],
      Tue: [],
      Wed: [],
      Thu: [],
      Fri: [],
      Sat: [],
    });
    getRoutines();
    setShowNewCalendar(true);
  };

  const handleShowEditCalendar = () => {
    // Cargar los datos del calendario actual al modal
    setEditingCalendarId(currentCalendar.id);
    setNewCalendar({
      name: currentCalendar.name,
      startDate: currentCalendar.startDate?.split("T")[0],
      endDate: currentCalendar.endDate?.split("T")[0],
    });
    setSelectedDepartmentId(currentCalendar.idDepartment || null);
    // Las rutinas ya están cargadas en routineDays desde getCalendarDetail
    getRoutines();
    setShowCurrentCalendar(false);
    setShowNewCalendar(true);
  };

  const handleDepartmentChange = (selected) => {
    if (selected) {
      // El dropdown retorna un objeto con 'value' que contiene el idDepartment
      const deptId = selected.value !== undefined ? selected.value : null;
      setSelectedDepartmentId(deptId);
    }
  };

  const handleCloseCurrentCalendar = () => {
    setShowCurrentCalendar(false);
    setRoutineDays({
      Sun: [],
      Mon: [],
      Tue: [],
      Wed: [],
      Thu: [],
      Fri: [],
      Sat: [],
    });
  };

  const handleShowCurrentCalendar = () => setShowCurrentCalendar(true);

  const handleCloseDeleteCalendarConfirm = () =>
    setShowDeleteCalendarConfirm(false);
  const handleShowDeleteCalendarConfirm = () =>
    setShowDeleteCalendarConfirm(true);

  //> Columns
  const calendarColumns = [
    {
      Header: " ",
      columns: [
        {
          Header: "",
          accessor: "idw",
          Cell: (props) => {
            return (
              <img
                className="rounded-circle table-profile-icon"
                src={calendarAvatar}
                alt="activity-user"
              ></img>
            );
          },
        },
        { Header: "ID", accessor: "id" },
        { Header: t("calendar.tableHeaderName"), accessor: "name" },
        {
          Header: t("calendar.tableHeaderDepartment"),
          accessor: "departmentName",
          Cell: (props) => <span>{props.value || "-"}</span>,
        },
        {
          Header: t("calendar.tableHeaderStartDate"),
          accessor: "startDate",
          Cell: (props) => <span>{formatDate(props.value)}</span>,
        },
        {
          Header: t("calendar.tableHeaderEndDate"),
          accessor: "endDate",
          Cell: (props) => <span>{formatDate(props.value)}</span>,
        },
        {
          Header: t("calendar.tableHeaderDetail"),
          accessor: "detalle",
          Cell: (props) => {
            return (
              <FaEye
                className="fa-table-icon"
                onClick={() => viewCurrentCalendar(props.cell.row.original)}
              />
            );
          },
        },
      ],
    },
  ];

  const routineColumns = [
    {
      Header: " ",
      columns: [
        { Header: "ID", accessor: "idRoutine" },
        {
          Header: t("calendar.routineTableHeaderRoutine"),
          accessor: "name",
          Cell: (props) => {
            return <div className="cell-rutine">{props.value}</div>;
          },
        },
        {
          Header: t("calendar.routineTableHeaderMorning"),
          accessor: "day",
          Cell: (props) => {
            return (
              <FaSun
                className="fa-table-icon"
                onClick={() => pushRoutine(props.cell.row.original, 0)}
              />
            );
          },
        },
        {
          Header: t("calendar.routineTableHeaderAfternoon"),
          accessor: "afternoon",
          Cell: (props) => {
            return (
              <FaMoon
                className="fa-table-icon"
                onClick={() => pushRoutine(props.cell.row.original, 1)}
              />
            );
          },
        },
        {
          Header: t("calendar.routineTableHeaderMixed"),
          accessor: "allday",
          Cell: (props) => {
            return (
              <FaGlobe
                className="fa-table-icon"
                onClick={() => pushRoutine(props.cell.row.original, 2)}
              />
            );
          },
        },
      ],
    },
  ];

  //> API Calls
  const getCalendars = useCallback(async () => {
    try {
      const res = await Axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/Calendar/list/${selectedClientId}`,
      );

      if (res.data.length === 0) {
        setData((prev) => ({
          ...prev,
          message: t("calendar.noCalendarsMessage"),
        }));
      }
      setCalendarRows(res.data.responseObject);
    } catch (e) {
      setData((prev) => ({
        ...prev,
        message: e.message,
      }));
    }
  }, [selectedClientId, t]);

  async function getCalendarDetail(calendarId) {
    try {
      const res = await Axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/Calendar/Detail/list/${calendarId}`,
      );

      if (res.data.responseObject.length === 0) {
        setData((prev) => ({
          ...prev,
          message: "No hay calendarios que mostrar.",
        }));
        return;
      }

      setRoutineDays({
        Sun: [],
        Mon: [],
        Tue: [],
        Wed: [],
        Thu: [],
        Fri: [],
        Sat: [],
      });

      const daysOfWeek = {
        Sun: [],
        Mon: [],
        Tue: [],
        Wed: [],
        Thu: [],
        Fri: [],
        Sat: [],
      };

      // Mapping numeric day values (1-7) to day names
      const dayNames = {
        1: "Sun", // Domingo
        2: "Mon", // Lunes
        3: "Tue", // Martes
        4: "Wed", // Miércoles
        5: "Thu", // Jueves
        6: "Fri", // Viernes
        7: "Sat", // Sábado
      };

      res.data.responseObject.forEach((event) => {
        const dayName = dayNames[event.day];
        if (dayName) {
          daysOfWeek[dayName].push(event);
        }
      });

      setRoutineDays(daysOfWeek);
    } catch (e) {
      setData((prev) => ({
        ...prev,
        message: e.message,
      }));
    }
  }

  async function getRoutines() {
    try {
      const url = selectedClientId
        ? `${process.env.REACT_APP_API_URL}/api/v1/Routine/list/${selectedClientId}`
        : `${process.env.REACT_APP_API_URL}/api/v1/Routine/list`;
      const res = await Axios.get(url);

      if (res.data.length === 0) {
        setData((prev) => ({
          ...prev,
          message: t("calendar.noRoutinesMessage"),
        }));
      }
      await setRoutineRows(res.data.responseObject);
    } catch (e) {
      setData((prev) => ({
        ...prev,
        message: e.message,
      }));
    }
  }

  async function deleteCalendar() {
    try {
      const res = await Axios.delete(
        `${process.env.REACT_APP_API_URL}/api/v1/Calendar/${currentCalendar.id}`,
      );
      if (res.status === 200) {
        const message =
          getApiResponseMessage(res) || t("calendar.calendarDeletedSuccess");
        notify(message, { type: "success" });
      }
    } catch (e) {
      const errorMessage = getApiErrorMessage(e);
      notify(errorMessage, { type: "error" });
    } finally {
      setShowCurrentCalendar(false);
      getCalendars();
      setCurrentCalendar(null);
      handleCloseDeleteCalendarConfirm();
    }
  }

  //> Handlers
  const handleNewCalendarSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      setCalendarValidated(true);
    } else {
      setCalendarValidated(false);

      const calendar = {
        ...newCalendar,
        idClient: selectedClientId,
        idDepartment: selectedDepartmentId,
        details: Object.keys(routineDays).flatMap((day, dayIndex) =>
          routineDays[day].map((routine) => ({
            day: dayIndex + 1,
            timeRange: routine.timeRange,
            idRoutine: routine.idRoutine,
          })),
        ),
        status: 1,
      };

      try {
        let res;
        if (editingCalendarId) {
          // Modo edición - usar PUT o POST con el ID en la ruta
          res = await Axios.put(
            `${process.env.REACT_APP_API_URL}/api/v1/Calendar/${editingCalendarId}`,
            calendar,
          );
          if (res.status === 200) {
            const message =
              getApiResponseMessage(res) ||
              t("calendar.calendarUpdatedSuccess");
            notify(message, { type: "success" });
          }
        } else {
          // Modo creación
          res = await Axios.post(
            `${process.env.REACT_APP_API_URL}/api/v1/Calendar`,
            calendar,
          );
          if (res.status === 200) {
            const message =
              getApiResponseMessage(res) || t("calendar.calendarAddedSuccess");
            notify(message, { type: "success" });
          }
        }

        getCalendars();
        setShowNewCalendar(false);
        setNewCalendar({});
        setEditingCalendarId(null);
      } catch (e) {
        const errorMessage = getApiErrorMessage(e);
        notify(errorMessage, { type: "error" });
      }
    }
  };

  const handleNewCalendarChange = (e) => {
    const { name, value } = e.target;
    setNewCalendar((prev) => ({ ...prev, [name]: value }));
  };

  const viewCurrentCalendar = (object) => {
    setCurrentCalendar(object);
    handleShowCurrentCalendar();
    getCalendarDetail(object.id);
  };

  const handleSelectCalendarTab = useCallback((key) => {
    setCurrentCalendarDay(key);
  }, []);

  const pushRoutine = useCallback(
    (routine, timeRange) => {
      var duple = {
        timeRange: timeRange,
        idRoutine: routine.idRoutine,
        name: routine.name,
      };

      setRoutineDays((prevRoutineDays) => {
        const updatedRoutineDays = { ...prevRoutineDays };
        if (!updatedRoutineDays[currentCalendarDay]) {
          updatedRoutineDays[currentCalendarDay] = [];
        }
        updatedRoutineDays[currentCalendarDay].push(duple);
        return updatedRoutineDays;
      });
    },
    [currentCalendarDay],
  );

  const removeRoutine = useCallback((day, idRoutine, timeRange) => {
    setRoutineDays((prevRoutineDays) => {
      const updatedRoutineDays = { ...prevRoutineDays };
      if (updatedRoutineDays[day]) {
        const index = updatedRoutineDays[day].findIndex(
          (r) => r.idRoutine === idRoutine && r.timeRange === timeRange,
        );
        if (index !== -1) {
          updatedRoutineDays[day].splice(index, 1);
        }
      }
      return updatedRoutineDays;
    });
  }, []);

  const reorderRoutines = useCallback((day, timeRange, fromIndex, toIndex) => {
    setRoutineDays((prevRoutineDays) => {
      const updatedRoutineDays = { ...prevRoutineDays };
      if (updatedRoutineDays[day]) {
        // Filter by timeRange to get the subset
        const filtered = updatedRoutineDays[day].filter(
          (r) => r.timeRange === timeRange,
        );
        const [movedItem] = filtered.splice(fromIndex, 1);
        filtered.splice(toIndex, 0, movedItem);

        // Replace the items with the same timeRange in the original array
        const otherTimeRange = updatedRoutineDays[day].filter(
          (r) => r.timeRange !== timeRange,
        );
        updatedRoutineDays[day] = [...otherTimeRange, ...filtered].sort(
          (a, b) => {
            if (a.timeRange !== b.timeRange) return a.timeRange - b.timeRange;
            return 0;
          },
        );
      }
      return updatedRoutineDays;
    });
  }, []);

  // Hover state for accordion effect on day tab sections
  const [hoveredTimeRange, setHoveredTimeRange] = useState(null);

  // Drag and drop handlers
  const [draggedItem, setDraggedItem] = useState(null);

  const handleDragStart = (day, index, timeRange) => {
    setDraggedItem({ day, index, timeRange });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (day, dropIndex, timeRange) => {
    if (
      draggedItem &&
      draggedItem.day === day &&
      draggedItem.timeRange === timeRange &&
      draggedItem.index !== dropIndex
    ) {
      reorderRoutines(day, timeRange, draggedItem.index, dropIndex);
    }
    setDraggedItem(null);
  };

  // Render routine item with drag and drop
  const renderRoutineItem = (item, index, day, timeRange) => {
    return (
      <div
        key={`${day}-${item.idRoutine}-${timeRange}-${index}`}
        draggable
        onDragStart={() => handleDragStart(day, index, timeRange)}
        onDragOver={handleDragOver}
        onDrop={() => handleDrop(day, index, timeRange)}
        style={{
          display: "flex",
          alignItems: "center",
          padding: "8px",
          marginBottom: "4px",
          backgroundColor: "#f8f9fa",
          borderRadius: "4px",
          cursor: "move",
          border:
            draggedItem?.day === day &&
            draggedItem?.index === index &&
            draggedItem?.timeRange === timeRange
              ? "2px dashed #007bff"
              : "1px solid #dee2e6",
        }}
      >
        <FaGripVertical
          style={{ marginRight: "8px", color: "#6c757d", cursor: "grab" }}
        />
        <span
          style={{ fontWeight: "bold", marginRight: "8px", color: "#495057" }}
        >
          {index + 1}.
        </span>
        <img
          className="rounded-circle table-profile-icon"
          src={routineAvatar}
          alt="routine"
          style={{ marginRight: "8px" }}
        />
        <span style={{ flex: 1 }}>{item.name}</span>
        <FaTimes
          onClick={() => removeRoutine(day, item.idRoutine, timeRange)}
          style={{
            cursor: "pointer",
            color: "#dc3545",
            fontSize: "18px",
          }}
          title="Eliminar rutina"
        />
      </div>
    );
  };

  const handleClientChange = (selected) => {
    if (selected) {
      // The dropdown now passes the full client object with idClient
      const newClientId = selected.idClient;
      // Allow setting to null when empty option is selected
      setSelectedClientId(newClientId);
      // Clear calendars when empty option is selected
      if (newClientId === null) {
        setCalendarRows([]);
      }
    }
  };

  //> Lifecycle
  useEffect(() => {
    if (selectedClientId) {
      setIsLoading(true);
      getCalendars().finally(() => setIsLoading(false));
    }
  }, [selectedClientId, getCalendars]);

  //> Render Day Tab Component with accordion effect
  const getFlexValue = (timeRange, dayKey) => {
    const items = routineDays[dayKey] || [];
    const hasItems = items.some((x) => x.timeRange === timeRange);
    const isHovered = hoveredTimeRange === timeRange;

    if (isHovered) return 4;
    if (hoveredTimeRange !== null) return hasItems ? 1 : 0.6;
    if (hasItems) return 1.8;
    return 1;
  };

  const sectionColors = {
    0: { bg: "#fff8e1", border: "#ffc107", label: "#f57f17" },
    1: { bg: "#e3f2fd", border: "#2196f3", label: "#1565c0" },
    2: { bg: "#f3e5f5", border: "#9c27b0", label: "#7b1fa2" },
  };

  const renderDayTab = (dayKey, dayName) => (
    <Tab eventKey={dayKey} title={dayName}>
      <div
        style={{ display: "flex", gap: "8px", marginTop: "10px" }}
        onMouseLeave={() => setHoveredTimeRange(null)}
      >
        {[
          {
            timeRange: 0,
            label: "Mañana",
            icon: <FaSun style={{ color: "#f57f17", marginRight: "6px" }} />,
          },
          {
            timeRange: 1,
            label: "Tarde",
            icon: <FaMoon style={{ color: "#1565c0", marginRight: "6px" }} />,
          },
          {
            timeRange: 2,
            label: "Mixto",
            icon: <FaGlobe style={{ color: "#7b1fa2", marginRight: "6px" }} />,
          },
        ].map((section) => {
          const colors = sectionColors[section.timeRange];
          const sectionItems = routineDays[dayKey].filter(
            (x) => x.timeRange === section.timeRange,
          );

          return (
            <div
              key={section.timeRange}
              onMouseEnter={() => setHoveredTimeRange(section.timeRange)}
              style={{
                flex: getFlexValue(section.timeRange, dayKey),
                transition: "flex 0.35s ease, box-shadow 0.3s ease",
                backgroundColor: colors.bg,
                borderRadius: "8px",
                border: `2px solid ${
                  hoveredTimeRange === section.timeRange
                    ? colors.border
                    : "#e0e0e0"
                }`,
                padding: "12px",
                minHeight: "80px",
                overflow: "hidden",
                boxShadow:
                  hoveredTimeRange === section.timeRange
                    ? `0 4px 12px ${colors.border}40`
                    : "none",
              }}
            >
              <h6
                style={{
                  color: colors.label,
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {section.icon}
                {section.label}
                {sectionItems.length > 0 && (
                  <span
                    style={{
                      marginLeft: "8px",
                      backgroundColor: colors.border,
                      color: "#fff",
                      borderRadius: "10px",
                      padding: "1px 8px",
                      fontSize: "0.7rem",
                    }}
                  >
                    {sectionItems.length}
                  </span>
                )}
              </h6>
              {sectionItems.map((item, index) =>
                renderRoutineItem(item, index, dayKey, section.timeRange),
              )}
            </div>
          );
        })}
      </div>
    </Tab>
  );

  return (
    <React.Fragment>
      {hasPermissions && <SideBar />}
      <Container>
        <NavBar />
        <Container>
          <Row>
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <Card.Title as="h5">{t("calendar.pageTitle")}</Card.Title>
                  {isSuperAdmin && (
                    <div style={{ minWidth: "250px" }}>
                      <DropDownClients
                        value={selectedClientId}
                        onChange={handleClientChange}
                        className="form-control"
                      />
                    </div>
                  )}
                </div>
              </Card.Header>
              <Card.Body>
                <div id="flexDiv">
                  <Button
                    id="btnLarge"
                    variant="primary"
                    onClick={handleShowNewCalendar}
                    disabled={!permissions.includes("Add/Update Calendar")}
                  >
                    {t("common.add")}
                  </Button>
                </div>
                <Col xs={12}>
                  {isLoading ? (
                    <div className="load-icon">
                      <ClipLoader
                        size={50}
                        color={"#0b5ed7"}
                        loading={isLoading}
                      />
                    </div>
                  ) : calendarRows.length === 0 ? (
                    <div className="no-data">{data?.message}</div>
                  ) : (
                    <DataTablePagination
                      columns={calendarColumns}
                      data={calendarRows}
                      onRowClick={(selectedRow) =>
                        setCurrentCalendar(selectedRow)
                      }
                      hiddenColumns={["id"]}
                    />
                  )}
                </Col>
              </Card.Body>
            </Card>
          </Row>
        </Container>
      </Container>

      {/* Modal: Create/Edit Calendar */}
      <Modal
        className="modal-xl"
        show={showNewCalendar}
        onHide={handleCloseNewCalendar}
      >
        <Form
          className="addForm"
          onSubmit={handleNewCalendarSubmit}
          noValidate
          validated={CalendarValidated}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {editingCalendarId
                ? t("calendar.editCalendarModalTitle")
                : t("calendar.createCalendarModalTitle")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="justify-content-md-center">
              <Row>
                <Col xs={12} md={6}>
                  {/* Name */}
                  <h6 className="steps-color">
                    {t("calendar.calendarNameStep")}
                  </h6>
                  <InputGroup className="mb-3">
                    <InputGroup.Text>{t("calendar.nameLabel")}</InputGroup.Text>
                    <Form.Control
                      required
                      minLength={3}
                      autoFocus
                      type="text"
                      name="name"
                      value={newCalendar?.name || ""}
                      placeholder={t("calendar.nameLabel")}
                      onChange={handleNewCalendarChange}
                    />
                  </InputGroup>
                </Col>
                <Col xs={12} md={6}>
                  {/* Department */}
                  <h6 className="steps-color">
                    {t("calendar.departmentOptionalStep")}
                  </h6>
                  <DropDownDepartments
                    value={selectedDepartmentId}
                    idClient={selectedClientId}
                    allowEmpty={true}
                    onChange={handleDepartmentChange}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  {/* Start Date */}
                  <h6 className="steps-color">{t("calendar.startDateStep")}</h6>
                  <Form.Control
                    required
                    minLength={5}
                    autoFocus
                    autoComplete="off"
                    type="date"
                    name="startDate"
                    value={newCalendar?.startDate || ""}
                    placeholder="Fecha Inicio"
                    onChange={handleNewCalendarChange}
                  />
                </Col>
                <Col xs={12} md={6}>
                  {/* End Date */}
                  <h6 className="steps-color">{t("calendar.endDateStep")}</h6>
                  <Form.Control
                    required
                    minLength={5}
                    autoFocus
                    autoComplete="off"
                    type="date"
                    name="endDate"
                    value={newCalendar?.endDate || ""}
                    placeholder="Fecha Final"
                    onChange={handleNewCalendarChange}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={8}>
                  <h6 className="steps-color">
                    {t("calendar.daySelectionStep")}
                  </h6>
                  <Tabs
                    variant="pills"
                    onSelect={handleSelectCalendarTab}
                    defaultActiveKey="Mon"
                    id="uncontrolled-tab-example"
                  >
                    {renderDayTab("Mon", t("calendar.mondayLabel"))}
                    {renderDayTab("Tue", t("calendar.tuesdayLabel"))}
                    {renderDayTab("Wed", t("calendar.wednesdayLabel"))}
                    {renderDayTab("Thu", t("calendar.thursdayLabel"))}
                    {renderDayTab("Fri", t("calendar.fridayLabel"))}
                    {renderDayTab("Sat", t("calendar.saturdayLabel"))}
                    {renderDayTab("Sun", t("calendar.sundayLabel"))}
                  </Tabs>
                </Col>
                <Col xs={12} md={4}>
                  <h6 className="steps-color">
                    {t("calendar.routineSelectionStep")}
                  </h6>
                  {routineRows.length === 0 ? (
                    <div className="no-data">{data?.message}</div>
                  ) : (
                    <DataTablePagination
                      columns={routineColumns}
                      data={routineRows}
                      onRowClick={() => {}}
                      hiddenColumns={["idRoutine"]}
                    />
                  )}
                </Col>
              </Row>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseNewCalendar}>
              {t("common.close")}
            </Button>
            <Button variant="primary" type="submit">
              {editingCalendarId ? t("common.update") : t("common.save")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal: View Current Calendar */}
      <Modal
        className="modal-xl"
        show={showCurrentCalendar}
        onHide={handleCloseCurrentCalendar}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("calendar.viewCalendarModalTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="justify-content-md-center">
            <Row>
              <Col xs={4}>
                {/* Name */}
                <div className="row m-t-30">
                  <div className="col-4">
                    <span>{t("calendar.calendarNameLabel")}</span>
                  </div>
                  <div className="col-8">
                    <h6>{currentCalendar?.name}</h6>
                  </div>
                </div>
              </Col>
              <Col xs={4}>
                {/* Start Date */}
                <div className="row m-t-30">
                  <div className="col-4">
                    <span>{t("calendar.calendarStartDateLabel")}</span>
                  </div>
                  <div className="col-8">
                    <h6>
                      {currentCalendar?.startDate
                        ? formatDate(currentCalendar?.startDate)
                        : ""}
                    </h6>
                  </div>
                </div>
              </Col>
              <Col xs={4}>
                {/* End Date */}
                <div className="row m-t-30">
                  <div className="col-4">
                    <span>{t("calendar.calendarEndDateLabel")}</span>
                  </div>
                  <div className="col-8">
                    <h6>
                      {currentCalendar?.endDate
                        ? formatDate(currentCalendar?.endDate)
                        : ""}
                    </h6>
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <Tabs
                  variant="pills"
                  onSelect={handleSelectCalendarTab}
                  defaultActiveKey="Mon"
                  id="uncontrolled-tab-example"
                >
                  {renderDayTab("Mon", t("calendar.mondayLabel"))}
                  {renderDayTab("Tue", t("calendar.tuesdayLabel"))}
                  {renderDayTab("Wed", t("calendar.wednesdayLabel"))}
                  {renderDayTab("Thu", t("calendar.thursdayLabel"))}
                  {renderDayTab("Fri", t("calendar.fridayLabel"))}
                  {renderDayTab("Sat", t("calendar.saturdayLabel"))}
                  {renderDayTab("Sun", t("calendar.sundayLabel"))}
                </Tabs>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col xs={4}>
                <Button
                  onClick={handleShowEditCalendar}
                  className="btn text-uppercase border btn-block btn-outline-primary"
                  disabled={!permissions.includes("Add/Update Calendar")}
                >
                  {t("common.edit")}
                </Button>
              </Col>
              <Col xs={4}></Col>
              <Col xs={4}>
                <Button
                  onClick={handleShowDeleteCalendarConfirm}
                  className="btn text-uppercase border btn-block btn-outline-danger"
                  disabled={!permissions.includes("Delete Calendar")}
                >
                  {t("common.delete")}
                </Button>
              </Col>
            </Row>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCurrentCalendar}>
            {t("common.close")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal: Delete Calendar Confirmation */}
      <Modal
        show={showDeleteCalendarConfirm}
        onHide={handleCloseDeleteCalendarConfirm}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("common.confirmDeleteTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("calendar.deleteCalendarConfirmMessage")}</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseDeleteCalendarConfirm}
          >
            {t("common.cancel")}
          </Button>
          <Button variant="danger" onClick={deleteCalendar}>
            {t("common.delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};
